import { supabase } from './supabase';
import * as Sentry from '@sentry/react-native';

interface ResolverResult<T> {
    data: T | null;
    error: any;
    wasCollision: boolean;
}

/**
 * Resolve a paper by short_id, handling collisions via slug matching.
 *
 * When multiple papers share the same short_id (8-char UUID prefix):
 * 1. If URL has a slug, pick the record whose slug best matches
 * 2. Fallback: pick the oldest record (by fetched_at)
 */
export async function resolvePaperByShortId(
    shortId: string,
    urlSlug: string | null,
    selectFields: string
): Promise<ResolverResult<any>> {
    // Simple query - no ORDER BY needed in common case (single match)
    const { data, error } = await supabase
        .from('paper')
        .select(selectFields)
        .eq('short_id', shortId);

    if (error) return { data: null, error, wasCollision: false };
    if (!data || data.length === 0) return { data: null, error: null, wasCollision: false };

    // Single match - no collision (common case)
    if (data.length === 1) return { data: data[0], error: null, wasCollision: false };

    // Collision detected (rare) - log to Sentry
    Sentry.captureMessage('Short-ID collision detected for paper', {
        level: 'warning',
        extra: { shortId, urlSlug, matchCount: data.length }
    });

    // Try slug matching first
    if (urlSlug) {
        const slugMatch = data.find((p: any) => p.slug === urlSlug);
        if (slugMatch) return { data: slugMatch, error: null, wasCollision: true };
    }

    // Fallback: sort client-side by fetched_at, pick oldest
    const sorted = data.sort((a: any, b: any) => {
        const aDate = new Date(a.fetched_at || 0);
        const bDate = new Date(b.fetched_at || 0);
        return aDate.getTime() - bDate.getTime();
    });
    return { data: sorted[0], error: null, wasCollision: true };
}

/**
 * Resolve a digest by short_id, handling collisions via slug matching.
 *
 * When multiple digests share the same short_id (8-char UUID prefix):
 * 1. If URL has a slug, try to match by project name generating the same slug
 * 2. Fallback: pick the oldest record (by created_at)
 */
export async function resolveDigestByShortId(
    shortId: string,
    urlSlug: string | null,
    selectFields: string
): Promise<ResolverResult<any>> {
    // Simple query - no ORDER BY needed in common case (single match)
    const { data, error } = await supabase
        .from('email_digest')
        .select(selectFields)
        .eq('short_id', shortId);

    if (error) return { data: null, error, wasCollision: false };
    if (!data || data.length === 0) return { data: null, error: null, wasCollision: false };

    // Single match - no collision (common case)
    if (data.length === 1) return { data: data[0], error: null, wasCollision: false };

    // Collision detected (rare) - log to Sentry
    Sentry.captureMessage('Short-ID collision detected for digest', {
        level: 'warning',
        extra: { shortId, urlSlug, matchCount: data.length }
    });

    // Try slug matching first (slug is derived from project.name)
    if (urlSlug) {
        const { generateDigestSlug } = await import('./paperUrl');
        const slugMatch = data.find((d: any) => {
            if (d.project?.name) {
                return generateDigestSlug(d.project.name) === urlSlug;
            }
            return false;
        });
        if (slugMatch) return { data: slugMatch, error: null, wasCollision: true };
    }

    // Fallback: sort client-side by created_at, pick oldest
    const sorted = data.sort((a: any, b: any) => {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        return aDate.getTime() - bDate.getTime();
    });
    return { data: sorted[0], error: null, wasCollision: true };
}

/**
 * Resolve a project_paper by short_id within a specific digest, handling collisions.
 *
 * This handles the case where multiple project_papers in a digest share the same short_id.
 * Uses efficient direct queries rather than fetching all papers.
 */
export async function resolveProjectPaperByShortId(
    shortId: string,
    digestId: string,
    urlSlug: string | null
): Promise<ResolverResult<{ project_paper_id: string }>> {
    // First, try to find by project_paper.short_id (most common case)
    const { data: ppMatches, error: ppError } = await supabase
        .from('project_paper')
        .select('id, slug, digest_papers!inner(digest_id)')
        .eq('digest_papers.digest_id', digestId)
        .eq('short_id', shortId)
        .order('identified_at', { ascending: true });

    if (ppError) return { data: null, error: ppError, wasCollision: false };

    if (ppMatches && ppMatches.length > 0) {
        // Found by project_paper.short_id
        if (ppMatches.length === 1) {
            return { data: { project_paper_id: ppMatches[0].id }, error: null, wasCollision: false };
        }

        // Collision - log and resolve
        Sentry.captureMessage('Short-ID collision for project_paper in digest', {
            level: 'warning',
            extra: { shortId, digestId, urlSlug, matchCount: ppMatches.length }
        });

        // Try slug matching
        if (urlSlug) {
            const slugMatch = ppMatches.find((pp: any) => pp.slug === urlSlug);
            if (slugMatch) return { data: { project_paper_id: slugMatch.id }, error: null, wasCollision: true };
        }

        // Fallback: first result (already sorted by identified_at)
        return { data: { project_paper_id: ppMatches[0].id }, error: null, wasCollision: true };
    }

    // Fallback: try to find by paper.short_id (for backwards compatibility)
    const { data: paperMatches, error: paperError } = await supabase
        .from('project_paper')
        .select('id, slug, paper!inner(short_id), digest_papers!inner(digest_id)')
        .eq('digest_papers.digest_id', digestId)
        .eq('paper.short_id', shortId)
        .order('identified_at', { ascending: true });

    if (paperError) return { data: null, error: paperError, wasCollision: false };
    if (!paperMatches || paperMatches.length === 0) {
        return { data: null, error: null, wasCollision: false };
    }

    if (paperMatches.length === 1) {
        return { data: { project_paper_id: paperMatches[0].id }, error: null, wasCollision: false };
    }

    // Collision on paper.short_id
    Sentry.captureMessage('Short-ID collision for paper in digest', {
        level: 'warning',
        extra: { shortId, digestId, urlSlug, matchCount: paperMatches.length }
    });

    if (urlSlug) {
        const slugMatch = paperMatches.find((pp: any) => pp.slug === urlSlug);
        if (slugMatch) return { data: { project_paper_id: slugMatch.id }, error: null, wasCollision: true };
    }

    return { data: { project_paper_id: paperMatches[0].id }, error: null, wasCollision: true };
}
